import React, { useEffect, useRef, useState } from 'react';
import './InvoiceLedgerEdit.css';
import api from '../services/api';
import useAutoSave, { autoSaveLabel } from '../hooks/useAutoSave';
import LedgerPayments from './LedgerPayments';

const CHAT_API_BASE = '/api/chat';

const INVOICE_STATUS_OPTIONS = ['Draft', 'Negotiation', 'Delivered', 'On Hold', 'Confirmed', 'Closed Accepted', 'Closed Lost', 'Closed Dead', 'Paid', 'Unpaid', 'Cancelled'];
const QUOTE_STAGE_OPTIONS = ['Draft', 'Negotiation', 'Delivered', 'On Hold', 'Confirmed', 'Closed Accepted', 'Closed Lost', 'Closed Dead'];
const PO_STATUS_OPTIONS = ['Draft', 'Pending Approval', 'Approved', 'Ordered', 'Received', 'Partially Received', 'Cancelled'];
const PO_CARRIER_OPTIONS = ['', 'USPS', 'UPS', 'FedEx', 'DHL', 'OnTrac', 'Other'];

// Clamp a numeric field to at most 2 decimal places (empty stays empty).
const round2 = (v) => {
  if (v === '' || v === null || v === undefined) return '';
  const n = parseFloat(v);
  return isNaN(n) ? '' : Math.round(n * 100) / 100;
};

// Note: 'description' is intentionally not a ledger column for invoices /
// proposals (it's still kept on each line item in state and saved to the DB —
// just not shown here). Purchase orders keep their own Description column below.
const ITEM_COLUMNS = [
  { key: 'image',      label: 'Img' },
  { key: 'part_number',label: 'SKU' },
  { key: 'name',       label: 'Item' },
  { key: 'quantity',   label: 'Qty' },
  { key: 'unit_price', label: 'Unit Price' },
  { key: 'line_total', label: 'Line Total' },
  { key: 'taxable',    label: 'Taxable' },
  { key: 'tax_profile', label: 'Tax Profile' },
];

// Proposals add a leading "Select" checkbox: unchecked lines are excluded from
// the Proposal PDF and from the Invoice conversion (default checked).
// Proposals also surface the per-line Description (invoices don't) — the field
// width is pinned via CSS so it doesn't collapse on narrow windows.
const QUOTE_ITEM_COLUMNS = [
  { key: 'selected',    label: 'Select' },
  { key: 'image',       label: 'Img' },
  { key: 'part_number', label: 'SKU' },
  { key: 'name',        label: 'Item' },
  { key: 'description', label: 'Description' },
  { key: 'quantity',    label: 'Qty' },
  { key: 'unit_price',  label: 'Unit Price' },
  { key: 'line_total',  label: 'Line Total' },
  { key: 'taxable',     label: 'Taxable' },
  { key: 'tax_profile', label: 'Tax Profile' },
];

const PO_ITEM_COLUMNS = [
  { key: 'image',                   label: 'Img' },
  { key: 'part_number',             label: 'SKU' },
  { key: 'name',                    label: 'Item' },
  // 'description' is intentionally omitted from the PO display columns — the
  // value is still loaded and saved (DB column unchanged), just not shown.
  { key: 'quantity',                label: 'Qty' },
  { key: 'unit_price',              label: 'Unit Price' },
  { key: 'line_total',              label: 'Line Total' },
  { key: 'expected_delivery_date',  label: 'Expected Delivery' },
  { key: 'estimated_ship_date',     label: 'Est. Ship Date' },
  { key: 'carrier',                 label: 'Carrier' },
  { key: 'tracking_number',         label: 'Tracking #' },
  { key: 'delivery_status_updates', label: 'Delivery / Status' },
];

const TOTALS_ROWS = [
  { key: 'subtotal', label: 'Subtotal' },
  { key: 'discount', label: 'Discount' },
  { key: 'tax',      label: 'Tax' },
  { key: 'shipping', label: 'Shipping' },
  { key: 'total',    label: 'Total' },
];

// Per-mode config: header fields, label, endpoint, storage key
const MODES = {
  invoice: {
    title: 'Invoice',
    endpoint: 'project-invoices.php',
    visKey: 'invoiceLedgerVisibility',
    headerFields: [
      { key: 'name',                       label: 'Invoice Name',  kind: 'text' },
      { key: 'number',                     label: 'Number',        kind: 'text' },
      { key: 'status',                     label: 'Status',        kind: 'select', options: INVOICE_STATUS_OPTIONS },
      { key: 'invoice_date',               label: 'Invoice Date',  kind: 'date' },
      { key: 'due_date',                   label: 'Due Date',      kind: 'date' },
      { key: 'billing_address_street',     label: 'Billing Street',kind: 'text' },
      { key: 'billing_address_city',       label: 'City',          kind: 'text' },
      { key: 'billing_address_state',      label: 'State',         kind: 'text' },
      { key: 'billing_address_postalcode', label: 'Postal Code',   kind: 'text' },
      { key: 'billing_address_country',    label: 'Country',       kind: 'text' },
      { key: 'description',                label: 'Description',   kind: 'textarea' },
    ],
  },
  quote: {
    title: 'Proposal',
    endpoint: 'project-quotes.php',
    visKey: 'quoteLedgerVisibility',
    itemColumns: QUOTE_ITEM_COLUMNS,
    headerFields: [
      { key: 'name',                       label: 'Proposal Name', kind: 'text' },
      { key: 'number',                     label: 'Number',        kind: 'text' },
      { key: 'stage',                      label: 'Stage',         kind: 'select', options: QUOTE_STAGE_OPTIONS },
      { key: 'expiration',                 label: 'Expiration',    kind: 'date' },
      { key: 'billing_address_street',     label: 'Billing Street',kind: 'text' },
      { key: 'billing_address_city',       label: 'City',          kind: 'text' },
      { key: 'billing_address_state',      label: 'State',         kind: 'text' },
      { key: 'billing_address_postalcode', label: 'Postal Code',   kind: 'text' },
      { key: 'billing_address_country',    label: 'Country',       kind: 'text' },
      { key: 'description',                label: 'Description',   kind: 'textarea' },
    ],
  },
  purchase_order: {
    title: 'Purchase Order',
    endpoint: 'purchase-orders-ledger.php',
    visKey: 'poLedgerVisibility',
    itemColumns: PO_ITEM_COLUMNS,
    headerFields: [
      { key: 'name',                        label: 'Subject',                kind: 'text' },
      { key: 'po_number',                   label: 'PO Number',              kind: 'text' },
      { key: 'status',                      label: 'Status',                 kind: 'select', options: PO_STATUS_OPTIONS },
      { key: 'vendor',                      label: 'Vendor',                 kind: 'text' },
      { key: 'order_date',                  label: 'Order Date',             kind: 'date' },
      { key: 'billing_address_street',      label: 'Billing Street',         kind: 'text' },
      { key: 'billing_address_city',        label: 'Billing City',           kind: 'text' },
      { key: 'billing_address_state',       label: 'Billing State',          kind: 'text' },
      { key: 'billing_address_postalcode',  label: 'Billing Postal Code',    kind: 'text' },
      { key: 'billing_address_country',     label: 'Billing Country',        kind: 'text' },
      { key: 'shipping_address_street',     label: 'Shipping Street',        kind: 'text' },
      { key: 'shipping_address_city',       label: 'Shipping City',          kind: 'text' },
      { key: 'shipping_address_state',      label: 'Shipping State',         kind: 'text' },
      { key: 'shipping_address_postalcode', label: 'Shipping Postal Code',   kind: 'text' },
      { key: 'shipping_address_country',    label: 'Shipping Country',       kind: 'text' },
      { key: 'description',                 label: 'Description',            kind: 'textarea' },
      { key: 'notes',                       label: 'Notes',                  kind: 'textarea' },
    ],
  },
};

async function authFetch(url, opts = {}) {
  const token = await api.ensureAuthenticated();
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
}

const fmtCurrency = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

// Auto-growing textarea that wraps text. Resizes to fit content on every change.
const AutoTextarea = ({ value, onChange, className = 'ledger-input', placeholder = '' }) => {
  const ref = useRef(null);
  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = (el.scrollHeight) + 'px';
  };
  useEffect(() => { resize(); }, [value]);
  return (
    <textarea
      ref={ref}
      className={className + ' wrap'}
      value={value || ''}
      placeholder={placeholder}
      rows={1}
      onChange={(e) => { onChange(e.target.value); }}
    />
  );
};

const loadVis = (storageKey) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) { return null; }
};
const saveVis = (storageKey, vis) => {
  try { localStorage.setItem(storageKey, JSON.stringify(vis)); } catch (e) {}
};

const defaultVisibility = (headerFields, itemColumns) => ({
  header:  Object.fromEntries(headerFields.map(f => [f.key, true])),
  columns: Object.fromEntries(itemColumns.map(c => [c.key, true])),
  totals:  Object.fromEntries(TOTALS_ROWS.map(r => [r.key, true])),
});

const InvoiceLedgerEdit = ({ invoiceId, recordId, mode = 'invoice', onCancel, onSaved, onConverted, onConvertedToPO }) => {
  const cfg = MODES[mode] || MODES.invoice;
  const itemColumns = cfg.itemColumns || ITEM_COLUMNS;
  const id = recordId || invoiceId; // back-compat with invoiceId prop name

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [showVisPanel, setShowVisPanel] = useState(false);

  // PO file attachments (packing slips, vendor invoices, spec sheets, …)
  const isPO = mode === 'purchase_order';
  const [attachments, setAttachments] = useState([]);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const attInputRef = useRef(null);

  const [visibility, setVisibility] = useState(() => {
    const persisted = loadVis(cfg.visKey);
    const def = defaultVisibility(cfg.headerFields, itemColumns);
    if (!persisted) return def;
    return {
      header:  { ...def.header,  ...(persisted.header  || {}) },
      columns: { ...def.columns, ...(persisted.columns || {}) },
      totals:  { ...def.totals,  ...(persisted.totals  || {}) },
    };
  });

  useEffect(() => { saveVis(cfg.visKey, visibility); }, [cfg.visKey, visibility]);

  const [header, setHeader] = useState(() =>
    Object.fromEntries(cfg.headerFields.map(f => [f.key, f.kind === 'select' ? (f.options?.[0] || '') : '']))
  );
  const [lineItems, setLineItems] = useState([]);

  // Line-item tax profiles (Product / Labor / Services / custom) for the Tax
  // Profile dropdown. Loaded once; the "+ Add new…" option appends to the list.
  const [taxProfiles, setTaxProfiles] = useState(['Product', 'Labor', 'Services']);
  useEffect(() => {
    if (isPO) return; // POs don't carry a tax profile
    (async () => {
      try {
        const res = await authFetch(`${CHAT_API_BASE}/tax-profiles.php`);
        const data = await res.json().catch(() => ({}));
        if (data.success && Array.isArray(data.profiles) && data.profiles.length) setTaxProfiles(data.profiles);
      } catch (e) { /* keep defaults */ }
    })();
  }, [isPO]);

  const addTaxProfile = async (idx) => {
    const name = (window.prompt('New tax profile name:') || '').trim();
    if (!name) return;
    try {
      const res = await authFetch(`${CHAT_API_BASE}/tax-profiles.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        if (Array.isArray(data.profiles)) setTaxProfiles(data.profiles);
        updateLineItem(idx, 'tax_profile', data.added || name);
      } else {
        setMsg({ type: 'error', text: data.message || 'Failed to add tax profile' });
      }
    } catch (e) {
      setMsg({ type: 'error', text: 'Failed to add tax profile: ' + e.message });
    }
  };

  // Companion fields not in cfg.headerFields — captured from the record's
  // top-level attributes. Needed for the proposal deposit flow (project_id).
  const [recordExtras, setRecordExtras] = useState({});

  // Stripe payment link state (invoice mode + proposal deposit mode).
  const [payLoading, setPayLoading] = useState(false);
  const [payLink, setPayLink] = useState(null);   // { url, session_id, status }
  const [payMsg, setPayMsg]  = useState(null);
  const currentUser = (() => {
    try { return JSON.parse(sessionStorage.getItem('user') || 'null'); }
    catch (e) { return null; }
  })();
  const currentUserId = currentUser?.id || null;
  const currentIsAdmin = !!currentUser?.isAdmin;

  const requestPaymentLink = async () => {
    if (!id) return;
    setPayLoading(true);
    setPayMsg(null);
    try {
      const res = await fetch(`${CHAT_API_BASE}/invoice-payment-link.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_id: id, user_id: currentUserId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setPayMsg({ type: 'error', text: data.message || 'Failed to create payment link' });
      } else {
        setPayLink(data);
        // Auto-copy to clipboard for one-click "paste into email" workflow.
        try { await navigator.clipboard.writeText(data.url); setPayMsg({ type: 'success', text: 'Payment link copied to clipboard.' }); }
        catch (e) { setPayMsg({ type: 'success', text: 'Payment link ready — click to copy.' }); }
      }
    } catch (e) {
      setPayMsg({ type: 'error', text: 'Failed to create payment link: ' + e.message });
    }
    setPayLoading(false);
  };

  const copyPaymentLink = async () => {
    if (!payLink?.url) return;
    try { await navigator.clipboard.writeText(payLink.url); setPayMsg({ type: 'success', text: 'Copied.' }); }
    catch (e) { setPayMsg({ type: 'error', text: 'Copy failed — select the URL and copy manually.' }); }
  };

  // Refund an existing paid invoice payment. Amount defaults to the full
  // remaining refundable balance; user can enter less for a partial refund.
  const requestRefund = async () => {
    const sp = recordExtras.stripe_payment;
    if (!sp || sp.status !== 'paid') return;
    const totalDollars = (sp.amount_cents / 100).toFixed(2);
    const remaining    = ((sp.amount_cents - (sp.refunded_amount_cents || 0)) / 100).toFixed(2);
    const amtRaw = window.prompt(`Refund how much? (Leave the default for a full refund of $${remaining}. This payment was $${totalDollars} total.)`, remaining);
    if (amtRaw === null) return;
    const amtDollars = parseFloat(String(amtRaw).replace(/[^0-9.\-]/g, ''));
    if (!amtDollars || amtDollars <= 0) { setPayMsg({ type: 'error', text: 'Enter a positive dollar amount.' }); return; }
    const reason = window.prompt('Reason for the refund? (Optional — stored on your record only.)', '');
    if (!window.confirm(`Refund $${amtDollars.toFixed(2)}? This moves money from your Stripe account back to the customer.`)) return;

    setPayLoading(true); setPayMsg(null);
    try {
      const res = await fetch(`${CHAT_API_BASE}/stripe-refund.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice_payment_id: sp.invoice_payment_id,
          user_id: currentUserId,
          amount_cents: Math.round(amtDollars * 100),
          reason: reason || null,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setPayMsg({ type: 'error', text: data.message || 'Refund failed' });
      } else {
        setPayMsg({ type: 'success', text: `Refund of $${(data.amount_cents / 100).toFixed(2)} ${data.status === 'succeeded' ? 'succeeded' : 'is ' + data.status}.` });
        // Reflect it locally so the button state updates without a page reload.
        setRecordExtras(prev => ({
          ...prev,
          stripe_payment: prev.stripe_payment ? {
            ...prev.stripe_payment,
            status: data.row_status,
            refunded_amount_cents: (prev.stripe_payment.refunded_amount_cents || 0) + data.amount_cents,
          } : prev.stripe_payment,
        }));
      }
    } catch (e) {
      setPayMsg({ type: 'error', text: 'Refund request failed: ' + e.message });
    }
    setPayLoading(false);
  };

  // Proposal deposits: money lands as a Retainer credit on the project.
  // Default suggestion is 50% of the proposal total, but the user can override.
  const requestProposalDeposit = async () => {
    if (!recordExtras.project_id) {
      setPayMsg({ type: 'error', text: 'This proposal isn\'t linked to a project — save + reopen it, or open the proposal from within a project.' });
      return;
    }
    const suggested = Math.max(50, Math.round(((total || 0) * 0.5) * 100) / 100);
    const raw = window.prompt(`Deposit amount (USD) — will be requested from the customer via Stripe and credited to this project's Retainer account.`, String(suggested));
    if (raw === null) return;
    const amt = parseFloat(String(raw).replace(/[^0-9.\-]/g, ''));
    if (!amt || amt <= 0) { setPayMsg({ type: 'error', text: 'Enter a positive dollar amount.' }); return; }
    setPayLoading(true); setPayMsg(null);
    try {
      const res = await fetch(`${CHAT_API_BASE}/rcs-deposit-link.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          project_id: recordExtras.project_id,
          account_type: 'retainers',
          amount: amt,
          user_id: currentUserId,
          // Tag the deposit with the originating proposal so the proposal
          // card can render a "Deposit paid / pending" badge later.
          source_parent_type: 'AOS_Quotes',
          source_parent_id: id,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setPayMsg({ type: 'error', text: data.message || 'Failed to create deposit link' });
      } else {
        setPayLink(data);
        try { await navigator.clipboard.writeText(data.url); setPayMsg({ type: 'success', text: 'Deposit link copied to clipboard. Money will land in this project\'s Retainer account.' }); }
        catch (e) { setPayMsg({ type: 'success', text: 'Deposit link ready — click Copy.' }); }
      }
    } catch (e) {
      setPayMsg({ type: 'error', text: 'Failed to create deposit link: ' + e.message });
    }
    setPayLoading(false);
  };
  const [tax, setTax] = useState('0');
  const [shipping, setShipping] = useState('0');
  const [discount, setDiscount] = useState('0');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const res = await authFetch(`${CHAT_API_BASE}/${cfg.endpoint}?id=${id}`);
        const data = await res.json();
        if (cancelled) return;
        if (!data.success) {
          setErr(data.error || `Failed to load ${cfg.title.toLowerCase()}`);
          setLoading(false);
          return;
        }
        const a = data.data?.attributes || {};
        const ba = a.billing_address || {};
        const sa = a.shipping_address || {};
        const nextHeader = {};
        cfg.headerFields.forEach(f => {
          let v;
          if (f.key.startsWith('billing_address_')) {
            v = ba[f.key.replace('billing_address_', '')] || '';
          } else if (f.key.startsWith('shipping_address_')) {
            v = sa[f.key.replace('shipping_address_', '')] || '';
          } else if (f.kind === 'date') {
            v = (a[f.key] || '').slice(0, 10);
          } else {
            v = a[f.key] ?? '';
          }
          nextHeader[f.key] = v;
        });
        setHeader(nextHeader);
        setRecordExtras({
          project_id: a.project_id || null,
          stripe_payment: a.stripe_payment || null,  // {invoice_payment_id, status, method, amount_cents, ...} or null
        });
        setLineItems((a.line_items || []).map(li => ({
          id: li.id,
          name: li.name || '',
          part_number: li.part_number || '',
          description: li.description || '',
          quantity: li.quantity ?? 1,
          unit_price: li.unit_price ?? 0,
          taxable: li.taxable === undefined || li.taxable === null ? true : !!parseInt(li.taxable, 10) || li.taxable === true,
          selected: li.selected === undefined || li.selected === null ? true : !!parseInt(li.selected, 10) || li.selected === true,
          tax_profile: li.tax_profile || 'Product',
          product_id: li.product_id || null,
          image_path: li.image_path || null,
          expected_delivery_date:  li.expected_delivery_date  || '',
          estimated_ship_date:     li.estimated_ship_date     || '',
          carrier:                 li.carrier                 || '',
          tracking_number:         li.tracking_number         || '',
          delivery_status_updates: li.delivery_status_updates || '',
        })));
        setTax(String(a.tax ?? '0'));
        setShipping(String(a.shipping ?? '0'));
        setDiscount(String(a.discount ?? '0'));
      } catch (e) {
        setErr('Network error loading invoice');
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, cfg.endpoint, cfg.title, cfg.headerFields]);

  // Attachments: POs use po-attachments.php (purchase_order_id); invoices &
  // proposals use ledger-attachments.php (parent_type/parent_id).
  const attListUrl = isPO
    ? `${CHAT_API_BASE}/po-attachments.php?purchase_order_id=${encodeURIComponent(id)}`
    : `${CHAT_API_BASE}/ledger-attachments.php?parent_type=${encodeURIComponent(mode)}&parent_id=${encodeURIComponent(id)}`;

  // Load existing attachments for this record.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(attListUrl);
        const data = await res.json();
        if (!cancelled && data.success) setAttachments(data.data || []);
      } catch (e) { /* attachments simply unavailable */ }
    })();
    return () => { cancelled = true; };
  }, [id, attListUrl]);

  const handleAttachChosen = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = '';
    if (!file || !id) return;
    setUploadingAtt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      let postUrl;
      if (isPO) {
        fd.append('purchase_order_id', id);
        postUrl = `${CHAT_API_BASE}/po-attachments.php`;
      } else {
        fd.append('parent_type', mode);
        fd.append('parent_id', id);
        postUrl = `${CHAT_API_BASE}/ledger-attachments.php`;
      }
      const res = await authFetch(postUrl, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success && data.data) {
        setAttachments(prev => [...prev, data.data]);
      } else {
        setMsg({ type: 'error', text: data.error || 'Upload failed' });
      }
    } catch (err) {
      setMsg({ type: 'error', text: 'Upload failed' });
    } finally {
      setUploadingAtt(false);
    }
  };

  const deleteAttachment = async (attId) => {
    if (!window.confirm('Remove this attachment?')) return;
    setAttachments(prev => prev.filter(a => a.id !== attId));
    try {
      const delBase = isPO ? 'po-attachments.php' : 'ledger-attachments.php';
      await authFetch(`${CHAT_API_BASE}/${delBase}?id=${encodeURIComponent(attId)}`, { method: 'DELETE' });
    } catch (e) { /* already removed locally */ }
  };

  const attIcon = (mime) => {
    if (mime && mime.startsWith('image/')) return '🖼️';
    if (mime && (mime.includes('word') || mime === 'application/msword')) return '📝';
    if (mime && (mime.includes('sheet') || mime.includes('excel'))) return '📊';
    return '📄';
  };

  const updateHeader = (k, v) => { setHeader(prev => ({ ...prev, [k]: v })); schedule(); };

  const updateLineItem = (idx, k, v) => {
    setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [k]: v } : li));
    schedule();
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, { name: '', part_number: '', description: '', quantity: 1, unit_price: 0, taxable: true, tax_profile: 'Product', selected: true }]);
    schedule();
  };

  const removeLineItem = (idx) => {
    setLineItems(prev => prev.filter((_, i) => i !== idx));
    schedule();
  };

  const toggleVis = (group, key) => {
    setVisibility(prev => ({
      ...prev,
      [group]: { ...prev[group], [key]: !prev[group][key] },
    }));
  };

  const setAllVis = (val) => {
    setVisibility({
      header:  Object.fromEntries(cfg.headerFields.map(f => [f.key, val])),
      columns: Object.fromEntries(itemColumns.map(c => [c.key, val])),
      totals:  Object.fromEntries(TOTALS_ROWS.map(r => [r.key, val])),
    });
  };

  const subtotal = lineItems.reduce((sum, li) => sum + (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0), 0);
  const taxN = parseFloat(tax) || 0;
  const shippingN = parseFloat(shipping) || 0;
  const discountN = parseFloat(discount) || 0;
  const total = subtotal - discountN + taxN + shippingN;

  // Auto-save: persist current state (reads latest state on each call). Throws on failure
  // so the hook can surface an error and retry on the next edit.
  const saveCurrent = async () => {
    // Retainer/SRISD invoice lines fund an RCS pool and need a linked project.
    if (mode === 'invoice') {
      const needsProject = lineItems.some(li => ['retainer', 'srisd'].includes(String(li.part_number || '').toLowerCase()));
      if (needsProject && !recordExtras.project_id) {
        setMsg({ type: 'error', text: 'Link this invoice to a project before adding Retainer or SRISD line items.' });
        return; // skip save — edits stay in state until a project is linked
      }
    }
    const payload = {
      user_id: currentUserId,
      attributes: {
        ...header,
        line_items: lineItems.map(li => ({
          id: li.id,
          name: li.name,
          part_number: li.part_number,
          description: li.description,
          quantity: parseFloat(li.quantity) || 0,
          unit_price: parseFloat(li.unit_price) || 0,
          taxable: !!li.taxable,
          tax_profile: li.tax_profile || 'Product',
          selected: li.selected !== false,
          expected_delivery_date:  li.expected_delivery_date  || null,
          estimated_ship_date:     li.estimated_ship_date     || null,
          carrier:                 li.carrier                 || null,
          tracking_number:         li.tracking_number         || null,
          delivery_status_updates: li.delivery_status_updates || null,
        })),
        tax: taxN,
        shipping: shippingN,
        discount: discountN,
      },
    };
    const res = await authFetch(`${CHAT_API_BASE}/${cfg.endpoint}?id=${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Save failed');
  };

  const { schedule, flush, status: autoStatus } = useAutoSave(saveCurrent);

  // Flush any pending edit, then close (so the parent refetches fresh data).
  const handleDone = async () => {
    await flush();
    (onSaved || onCancel) && (onSaved || onCancel)();
  };

  if (loading) return <div className="invoice-ledger"><div className="ledger-loading">Loading {cfg.title.toLowerCase()}...</div></div>;
  if (err) return <div className="invoice-ledger"><div className="ledger-error">{err}</div></div>;

  const renderHeaderField = (f) => {
    const v = header[f.key];
    if (f.kind === 'select') {
      return (
        <select className="ledger-input" value={v} onChange={e => updateHeader(f.key, e.target.value)}>
          {f.options.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      );
    }
    if (f.kind === 'date') {
      return (
        <input className="ledger-input" type="date" value={v} onChange={e => updateHeader(f.key, e.target.value)} />
      );
    }
    // text + textarea both use the auto-wrapping textarea
    return <AutoTextarea value={v} onChange={(val) => updateHeader(f.key, val)} />;
  };

  // Totals block — rendered inline for POs, or inside the right sidepanel for
  // invoices / proposals.
  const totalsTable = (
    <table className="ledger-table totals-table">
      <tbody>
        {visibility.totals.subtotal && (
          <tr>
            <td className="lbl">Subtotal</td>
            <td className="num line-total">{fmtCurrency(subtotal)}</td>
          </tr>
        )}
        {visibility.totals.discount && (
          <tr>
            <td className="lbl">Discount</td>
            <td className="num"><input className="ledger-input num" type="number" min="0" step="0.01" value={discount} onChange={e => { setDiscount(e.target.value); schedule(); }} /></td>
          </tr>
        )}
        {visibility.totals.tax && (
          <tr>
            <td className="lbl">Tax</td>
            <td className="num"><input className="ledger-input num" type="number" min="0" step="0.01" value={tax} onChange={e => { setTax(e.target.value); schedule(); }} /></td>
          </tr>
        )}
        {visibility.totals.shipping && (
          <tr>
            <td className="lbl">Shipping</td>
            <td className="num"><input className="ledger-input num" type="number" min="0" step="0.01" value={shipping} onChange={e => { setShipping(e.target.value); schedule(); }} /></td>
          </tr>
        )}
        {visibility.totals.total && (
          <tr className="grand-total">
            <td className="lbl">Total</td>
            <td className="num line-total">{fmtCurrency(total)}</td>
          </tr>
        )}
      </tbody>
    </table>
  );

  return (
    <div className="invoice-ledger">
      {/* Visibility toolbar */}
      <div className="ledger-toolbar">
        <button className="vis-toggle-btn" onClick={() => setShowVisPanel(s => !s)}>
          {showVisPanel ? 'Hide field selector' : 'Show/Hide fields'}
        </button>
        {showVisPanel && (
          <div className="vis-panel">
            <div className="vis-panel-actions">
              <button className="vis-link" onClick={() => setAllVis(true)}>Show all</button>
              <button className="vis-link" onClick={() => setAllVis(false)}>Hide all</button>
            </div>
            <div className="vis-panel-grid">
              <div>
                <h4>Header fields</h4>
                {cfg.headerFields.map(f => (
                  <label key={f.key} className="vis-option">
                    <input type="checkbox" checked={!!visibility.header[f.key]} onChange={() => toggleVis('header', f.key)} />
                    {f.label}
                  </label>
                ))}
              </div>
              <div>
                <h4>Line item columns</h4>
                {itemColumns.map(c => (
                  <label key={c.key} className="vis-option">
                    <input type="checkbox" checked={!!visibility.columns[c.key]} onChange={() => toggleVis('columns', c.key)} />
                    {c.label}
                  </label>
                ))}
              </div>
              <div>
                <h4>Totals rows</h4>
                {TOTALS_ROWS.map(r => (
                  <label key={r.key} className="vis-option">
                    <input type="checkbox" checked={!!visibility.totals[r.key]} onChange={() => toggleVis('totals', r.key)} />
                    {r.label}
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {msg && <div className={`ledger-message ${msg.type}`}>{msg.text}</div>}

      <div className="ledger-body has-sidepanel">
      <div className="ledger-main">

      {/* Header — compact wrapping grid: short fields share rows to save
          vertical space; textareas span the full width on their own line.
          Each field stays individually toggleable. */}
      <div className="ledger-header-grid">
        {cfg.headerFields.filter(f => visibility.header[f.key]).map(f => (
          <div key={f.key} className={`ledger-header-field${f.kind === 'textarea' ? ' full' : ''}`}>
            <label className="ledger-header-label">{f.label}</label>
            {renderHeaderField(f)}
          </div>
        ))}
      </div>

      {/* Line items — wrapped in a horizontal-scroll container so columns keep a
          readable minimum width and the table scrolls right instead of shrinking. */}
      <div className="items-table-scroll">
      <table className="ledger-table items-table">
        <thead>
          <tr>
            {itemColumns.filter(c => visibility.columns[c.key]).map(c => (
              <th
                key={c.key}
                className={`col-${c.key} ` + (
                  c.key === 'quantity' || c.key === 'unit_price' || c.key === 'line_total' ? 'num'
                  : (c.key === 'taxable' || c.key === 'selected') ? 'chk'
                  : ''
                )}
              >
                {c.label}
              </th>
            ))}
            <th className="del"></th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((li, idx) => {
            const lineTotal = (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0);
            const cellFor = (key) => {
              switch (key) {
                case 'image':
                  return (
                    <td key="image" className="ledger-img-cell">
                      {li.image_path ? (
                        <img src={'/' + li.image_path} alt="" className="ledger-img-thumb" />
                      ) : (
                        <span className="ledger-img-placeholder">—</span>
                      )}
                    </td>
                  );
                case 'part_number': {
                  // Manual line items (no linked catalog product) get a SKU
                  // dropdown of the common non-product charge types so the
                  // operator can add labor / consulting / etc. without free-
                  // typing every time. Rows that ARE a catalog product (have
                  // product_id) keep the plain SKU text so we don't overwrite
                  // the real SKU value.
                  //
                  // Presets differ per mode:
                  //   invoice / quote : Consulting, Retainer, SRISD (all three
                  //                     customer-facing — a proposal converts
                  //                     into an invoice, and Retainer/SRISD
                  //                     invoice lines draw from the project's
                  //                     RCS pool on save)
                  //   purchase_order  : Consulting, SRISD (Retainer excluded —
                  //                     POs are outbound to vendors, so a
                  //                     customer-held retainer never applies;
                  //                     matches the PO payment-methods filter)
                  //   other modes     : plain text field
                  const skuPresets =
                      (mode === 'invoice' || mode === 'quote') ? ['Consulting', 'Retainer', 'SRISD']
                    : mode === 'purchase_order'                ? ['Consulting', 'SRISD']
                    : null;
                  if (!skuPresets || li.product_id) {
                    return <td key="part_number"><input className="ledger-input" type="text" value={li.part_number || ''} onChange={e => updateLineItem(idx, 'part_number', e.target.value)} /></td>;
                  }
                  const SKU_PRESETS = skuPresets;
                  const isCustomSku = !!li._skuCustom || (!!li.part_number && !SKU_PRESETS.includes(li.part_number));
                  const skuSelVal = isCustomSku ? '__custom__' : (li.part_number || '');
                  return (
                    <td key="part_number">
                      <select
                        className="ledger-input ledger-sku-select"
                        value={skuSelVal}
                        onChange={e => {
                          const v = e.target.value;
                          setLineItems(prev => prev.map((it, i) => i === idx
                            ? (v === '__custom__' ? { ...it, _skuCustom: true } : { ...it, _skuCustom: false, part_number: v })
                            : it));
                          schedule();
                        }}
                      >
                        <option value="">—</option>
                        {SKU_PRESETS.map(s => <option key={s} value={s}>{s}</option>)}
                        <option value="__custom__">Custom…</option>
                      </select>
                      {isCustomSku && (
                        <input
                          className="ledger-input ledger-sku-custom"
                          type="text"
                          placeholder="Enter SKU"
                          value={li.part_number || ''}
                          onChange={e => updateLineItem(idx, 'part_number', e.target.value)}
                        />
                      )}
                    </td>
                  );
                }
                case 'name':
                  return <td key="name"><AutoTextarea value={li.name} onChange={(v) => updateLineItem(idx, 'name', v)} /></td>;
                case 'description':
                  return <td key="description" className="col-description"><AutoTextarea value={li.description} onChange={(v) => updateLineItem(idx, 'description', v)} /></td>;
                case 'quantity':
                  return <td key="quantity" className="num"><input className="ledger-input num" type="number" min="0" step="0.01" value={li.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} onBlur={e => updateLineItem(idx, 'quantity', round2(e.target.value))} /></td>;
                case 'unit_price':
                  return <td key="unit_price" className="num"><input className="ledger-input num" type="number" min="0" step="0.01" value={li.unit_price} onChange={e => updateLineItem(idx, 'unit_price', e.target.value)} onBlur={e => updateLineItem(idx, 'unit_price', round2(e.target.value))} /></td>;
                case 'line_total':
                  return <td key="line_total" className="num line-total">{fmtCurrency(lineTotal)}</td>;
                case 'taxable':
                  return <td key="taxable" className="chk"><input type="checkbox" checked={!!li.taxable} onChange={e => updateLineItem(idx, 'taxable', e.target.checked)} /></td>;
                case 'tax_profile': {
                  const tp = li.tax_profile || 'Product';
                  // The list may not yet include a value loaded from an older
                  // record (e.g. a profile deleted since) — keep it selectable.
                  const opts = taxProfiles.includes(tp) ? taxProfiles : [tp, ...taxProfiles];
                  return (
                    <td key="tax_profile">
                      <select
                        className="ledger-input"
                        value={tp}
                        onChange={e => {
                          if (e.target.value === '__add__') { addTaxProfile(idx); return; }
                          updateLineItem(idx, 'tax_profile', e.target.value);
                        }}
                      >
                        {opts.map(p => <option key={p} value={p}>{p}</option>)}
                        <option value="__add__">+ Add new…</option>
                      </select>
                    </td>
                  );
                }
                case 'selected':
                  return <td key="selected" className="chk"><input type="checkbox" checked={li.selected !== false} onChange={e => updateLineItem(idx, 'selected', e.target.checked)} /></td>;
                case 'expected_delivery_date':
                  return <td key="expected_delivery_date"><input className="ledger-input" type="date" value={li.expected_delivery_date || ''} onChange={e => updateLineItem(idx, 'expected_delivery_date', e.target.value)} /></td>;
                case 'estimated_ship_date':
                  return <td key="estimated_ship_date"><input className="ledger-input" type="date" value={li.estimated_ship_date || ''} onChange={e => updateLineItem(idx, 'estimated_ship_date', e.target.value)} /></td>;
                case 'carrier':
                  return (
                    <td key="carrier">
                      <select className="ledger-input" value={li.carrier || ''} onChange={e => updateLineItem(idx, 'carrier', e.target.value)}>
                        {PO_CARRIER_OPTIONS.map(opt => <option key={opt} value={opt}>{opt || '—'}</option>)}
                      </select>
                    </td>
                  );
                case 'tracking_number':
                  return <td key="tracking_number"><input className="ledger-input" type="text" value={li.tracking_number || ''} onChange={e => updateLineItem(idx, 'tracking_number', e.target.value)} /></td>;
                case 'delivery_status_updates':
                  return <td key="delivery_status_updates"><AutoTextarea value={li.delivery_status_updates} onChange={(v) => updateLineItem(idx, 'delivery_status_updates', v)} /></td>;
                default:
                  return null;
              }
            };
            return (
              <tr key={idx} className={idx % 2 === 0 ? 'row-even' : 'row-odd'}>
                {itemColumns.filter(c => visibility.columns[c.key]).map(c => cellFor(c.key))}
                <td className="del"><button className="row-del-btn" onClick={() => removeLineItem(idx)} aria-label="Remove">×</button></td>
              </tr>
            );
          })}
          <tr className="add-row">
            <td colSpan={itemColumns.filter(c => visibility.columns[c.key]).length + 1}>
              <button className="add-row-btn" onClick={addLineItem}>+ Add Line Item</button>
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      {/* Totals + payments live in the right-hand sidepanel for every mode
          (invoice / proposal / purchase_order) so partial-payment tracking
          is consistent across the ledger. */}

      {id && (
        <div className="ledger-attachments">
          <div className="ledger-attachments-head">
            <h4>Attachments</h4>
            <input
              ref={attInputRef}
              type="file"
              accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
              style={{ display: 'none' }}
              onChange={handleAttachChosen}
            />
            <button
              className="ledger-btn ledger-btn-attach"
              disabled={uploadingAtt}
              onClick={() => attInputRef.current && attInputRef.current.click()}
            >
              {uploadingAtt ? 'Uploading…' : '+ Attach file'}
            </button>
          </div>
          {attachments.length === 0 ? (
            <p className="ledger-attachments-empty">No files attached. Attach a PDF, image, or document (max 20MB).</p>
          ) : (
            <ul className="ledger-attachments-list">
              {attachments.map(att => (
                <li key={att.id} className="ledger-attachment">
                  <a href={att.file_url} target="_blank" rel="noopener noreferrer" title={att.name}>
                    <span className="att-icon">{attIcon(att.mime)}</span>
                    <span className="att-name">{att.name}</span>
                  </a>
                  <button className="att-del" title="Remove attachment" onClick={() => deleteAttachment(att.id)}>×</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      </div>{/* .ledger-main */}

      <aside className="ledger-sidepanel">
        {totalsTable}
        <LedgerPayments parentType={mode} parentId={id} total={total} />
      </aside>
      </div>{/* .ledger-body */}

      <div className="ledger-actions">
        <span className={`ledger-autosave ledger-autosave-${autoStatus}`}>{autoSaveLabel(autoStatus)}</span>
        {mode === 'invoice' && (
          <button className="ledger-btn ledger-btn-pay" disabled={payLoading} onClick={requestPaymentLink}>
            {payLoading ? 'Creating link…' : (payLink ? 'Refresh Payment Link' : 'Create Payment Link')}
          </button>
        )}
        {mode === 'invoice' && currentIsAdmin
          && recordExtras.stripe_payment
          && (recordExtras.stripe_payment.status === 'paid' || recordExtras.stripe_payment.status === 'partially_refunded')
          && (
          <button className="ledger-btn ledger-btn-refund" disabled={payLoading} onClick={requestRefund}>
            {payLoading ? 'Working…' : (recordExtras.stripe_payment.status === 'partially_refunded' ? 'Refund More' : 'Refund')}
          </button>
        )}
        {mode === 'quote' && (
          <button className="ledger-btn ledger-btn-pay" disabled={payLoading} onClick={requestProposalDeposit}>
            {payLoading ? 'Creating link…' : 'Request Deposit'}
          </button>
        )}
        {<button className="ledger-btn ledger-btn-pdf" onClick={async () => {
          try {
            const pdfType = mode === 'quote' ? 'quote' : (mode === 'purchase_order' ? 'purchase_order' : 'invoice');
            const visParam = encodeURIComponent(JSON.stringify(visibility));
            const res = await authFetch(`${CHAT_API_BASE}/generate-pdf.php?type=${pdfType}&id=${id}&visibility=${visParam}`);
            if (!res.ok) throw new Error('PDF generation failed');
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${cfg.title}_${id}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
          } catch (e) {
            setMsg({ type: 'error', text: 'Failed to generate PDF: ' + e.message });
          }
        }}>Download PDF</button>}
        {mode === 'quote' && <button className="ledger-btn ledger-btn-convert" onClick={async () => {
          try {
            await flush(); // persist the latest Select flags before converting
            const res = await authFetch(`${CHAT_API_BASE}/convert-quote-to-po.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ quote_id: id }),
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.message || data.error || 'Conversion failed');
            setMsg({ type: 'success', text: data.message || 'Purchase orders created.' });
            if (onConvertedToPO) onConvertedToPO(data.purchase_orders || []);
          } catch (e) {
            setMsg({ type: 'error', text: 'Failed to convert to PO: ' + e.message });
          }
        }}>Convert to PO</button>}
        {mode === 'quote' && <button className="ledger-btn ledger-btn-convert" onClick={async () => {
          try {
            await flush(); // persist the latest Select flags before converting
            const res = await authFetch(`${CHAT_API_BASE}/project-invoices.php`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ from_quote_id: id }),
            });
            const data = await res.json();
            if (!res.ok || !(data.success || data.id)) throw new Error(data.error || 'Conversion failed');
            setMsg({ type: 'success', text: 'Invoice created from the selected items.' });
            if (onConverted) onConverted(data.id || (data.data && data.data.id));
          } catch (e) {
            setMsg({ type: 'error', text: 'Failed to convert to invoice: ' + e.message });
          }
        }}>Convert to Invoice</button>}
        <button className="ledger-btn ledger-btn-save" onClick={handleDone}>Done</button>
      </div>

      {(mode === 'invoice' || mode === 'quote') && (payLink || payMsg) && (
        <div className="ledger-pay-strip">
          {payMsg && (
            <div className={`ledger-pay-msg ledger-pay-msg-${payMsg.type}`}>{payMsg.text}</div>
          )}
          {payLink?.url && (
            <div className="ledger-pay-link">
              <input readOnly value={payLink.url} onFocus={e => e.target.select()} />
              <button className="ledger-btn ledger-btn-pdf" onClick={copyPaymentLink}>Copy</button>
              <a className="ledger-btn ledger-btn-pdf" href={payLink.url} target="_blank" rel="noopener noreferrer">Open</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InvoiceLedgerEdit;
